import {
    selectAutomaticTranslationPreferred,
    selectPreferredLanguageCode
} from 'tradera/state/language/selectors';
import { getTraderaShopProductData } from './api';
import { logger } from 'packages/logger';
import { fetchDescription, getItemsFromSeller } from '../view-item/api';
import {
    beginLoadingTranslatedDescription,
    failedLoadingTranslatedDescription,
    setCurrentDescriptionLanguageCodeIso2,
    setIsLoading,
    setItemDetails,
    setProduct,
    setSellerItems,
    setTranslatedDescription,
    setTranslationOnDemandDisabled
} from './reducer';
import { translatedDescriptionStates } from '../view-item/reducer';
import {
    selectTranslatedDescription,
    selectTranslatedDescriptionState
} from './selectors';
import { selectShippingRegionCountryCodeIso2 } from 'tradera/state/shipping-region/selectors';
import { isSwedenCountryCodeOrUndefined } from 'tradera/localization/countrycode';
import { areLanguageCodesIso2Equal } from 'tradera/localization/languagecode';
import { selectIsFeatureEnabled } from 'tradera/state/environment/selectors';
import { wishlist } from 'tradera/state/items/reducer';

export const fetchTraderaShopProductData =
    (urlSlug) => async (dispatch, getState) => {
        const preferredLanguageCode = selectPreferredLanguageCode(getState());
        dispatch(setIsLoading(true));
        try {
            const traderaShopProduct = await getTraderaShopProductData(
                urlSlug,
                preferredLanguageCode
            );
            dispatch(handleTraderaShopProduct(traderaShopProduct));
            dispatch(setIsLoading(false));
        } catch (error) {
            const status = error.response && error.response.status;
            if (status === 404) {
                // Handle server side.
                window.location.reload();
            } else {
                logger(error);
            }
        }
    };

export const handleTraderaShopProduct = (traderaShopProduct) => (dispatch) => {
    const { product, item } = traderaShopProduct;
    dispatch(setProduct(product));
    dispatch(setItemDetails(item.itemDetails));
    dispatch(setTranslation(item));
    if (item.itemDetails.inWishList) {
        dispatch(wishlist({ itemId: item.itemDetails.itemId, exists: true }));
    }
};

const setTranslation = (itemModel) => (dispatch, getState) => {
    const state = getState();
    const shippingRegion = selectShippingRegionCountryCodeIso2(state);
    const preferredLanguageCodeIso2 = selectPreferredLanguageCode(state);
    const disabledForEnglishLangSwedes =
        isSwedenCountryCodeOrUndefined(shippingRegion) &&
        areLanguageCodesIso2Equal(preferredLanguageCodeIso2, 'en');
    const automaticTranslationPreferred =
        selectAutomaticTranslationPreferred(state);
    const descriptionLanguageCodeIso2 =
        itemModel.itemDetails.descriptionLanguageCodeIso2;
    const shouldTranslateDescriptionOnLoad =
        automaticTranslationPreferred &&
        preferredLanguageCodeIso2 !== descriptionLanguageCodeIso2 &&
        !disabledForEnglishLangSwedes;
    dispatch(
        setCurrentDescriptionLanguageCodeIso2(
            shouldTranslateDescriptionOnLoad
                ? preferredLanguageCodeIso2
                : descriptionLanguageCodeIso2
        )
    );
};

export const loadTranslatedDescription =
    (itemId, languageCodeIso2, descriptionLanguageCodeIso2) =>
    async (dispatch, getState) => {
        const isOnDemandItemDescriptionTranslation = selectIsFeatureEnabled(
            'on-demand-item-description-translation'
        )(getState());

        if (!languageCodeIso2 || !isOnDemandItemDescriptionTranslation) {
            return;
        }

        if (
            languageCodeIso2.toLowerCase() ===
            descriptionLanguageCodeIso2.toLowerCase()
        ) {
            return;
        }

        if (
            selectTranslatedDescriptionState(getState()) ===
                translatedDescriptionStates.LOADING ||
            selectTranslatedDescription(getState())?.languageCodeIso2 ===
                languageCodeIso2
        ) {
            return;
        }

        dispatch(beginLoadingTranslatedDescription());

        try {
            const result = await fetchDescription(itemId, languageCodeIso2);
            if (result.data.translations.isTranslationOnDemandDisabled) {
                dispatch(setTranslationOnDemandDisabled());
                return;
            }

            dispatch(
                setTranslatedDescription({
                    title: result.data.translations.title,
                    description: result.data.translations.description,
                    additionalDescriptions:
                        result.data.translations.additionalDescriptions,
                    languageCodeIso2: result.data.translations.languageCodeIso2,
                    providerName: 'Google Translate',
                    shippingTerms: result.data.translations.shippingTerms
                })
            );
        } catch (e) {
            dispatch(failedLoadingTranslatedDescription());
        }
    };

export const fetchItemsFromSeller =
    ({ sellerMemberId, size }) =>
    async (dispatch, getState) => {
        try {
            const preferredLanguageCode =
                selectPreferredLanguageCode(getState());
            const params = {
                sellerMemberId,
                size,
                searchResultLanguageCodeIso2: preferredLanguageCode
            };
            const result = await getItemsFromSeller(
                params,
                preferredLanguageCode
            );
            const { items, itemsMatched } = result.data.result;
            dispatch(setSellerItems({ items, itemsMatched }));
        } catch (e) {
            dispatch(setSellerItems(null));
        }
    };
